"use client";

import { TopShop } from "@faire/design-tokens";
import { TopShopEducationTooltip } from "@faire/retailer-visitor-shared/components/TopShop/TopShopTooltip";
import { useTopShopVisibility } from "@faire/retailer-visitor-shared/components/TopShop/useTopShopVisibility";
import { useEventsStore } from "@faire/retailer-visitor-shared/stores/domain/Events/shared/EventsStore";
import {
  Avatar,
  AvatarSize,
  IProps as IAvatarProps,
} from "@faire/web--source/slate/Avatar";
import { TrophyIcon } from "@faire/web--source/slate/icons/Trophy";
import { Row } from "@faire/web--source/slate/Layout";
import { isResponsiveProp } from "@faire/web--source/slate/Theme/ResponsiveProp";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import React from "react";
import styled from "styled-components";

interface IProps extends IAvatarProps {
  isTopShop?: boolean;
  displayTooltip?: boolean;
  iconSize?: string;
  hideIcon?: boolean;
  hideTopShopBorder?: boolean;
}

export const BrandAvatar: React.FC<IProps> = ({
  isTopShop,
  iconSize,
  displayTooltip,
  hideIcon,
  hideTopShopBorder,
  ...avatarProps
}) => {
  const viewport = useViewport();
  const responsiveSize = useResponsiveValue(
    isResponsiveProp(avatarProps.size)
      ? avatarProps.size
      : { mobileAndAbove: avatarProps.size }
  );
  const iconSizeMap: Record<AvatarSize, string> = {
    "xx-small": "16px",
    "x-small": "16px",
    smaller: "16px",
    small: "16px",
    medium: "22px",
    large: "22px",
    "x-large": "22px",
  };
  const { nextOrActiveEventTheme: eventTheme } = useEventsStore([
    "nextOrActiveEventTheme",
  ]);

  const { isTopShopContentVisibleForBrand } = useTopShopVisibility(isTopShop);

  if (isTopShopContentVisibleForBrand) {
    const fontSize = iconSize ?? iconSizeMap[responsiveSize];
    const baseIcon = <TrophyIcon active fontSize={fontSize} titleAccess="" />;
    const icon =
      displayTooltip && viewport.isDesktopAndAbove ? (
        <TopShopEducationTooltip icon={baseIcon} />
      ) : (
        baseIcon
      );
    return (
      <TopShopAvatarContainer>
        <TopShopAvatar
          {...avatarProps}
          iconData={
            hideIcon
              ? undefined
              : {
                  position: "bottomRight",
                  icon,
                }
          }
          borderColor={hideTopShopBorder ? undefined : TopShop.shade.green}
          theme={eventTheme}
        />
      </TopShopAvatarContainer>
    );
  }

  return <Avatar {...avatarProps} theme={eventTheme} />;
};

const TopShopAvatar = styled(Avatar)`
  background-color: unset;
`;

const TopShopAvatarContainer = styled(Row)`
  position: relative;
  z-index: 1;
  height: fit-content;
`;
